/**
 * URLService
 *
 * Handles URL construction and environment detection (local vs production).
 * All methods are stateless and pure.
 *
 * Extracted from: ResultDisplay.tsx (lines 47-77)
 */

import { getApiBaseUrl } from './urlResolver';
import { isLocalMode as checkLocalMode } from '../utils/apiMode';

export class URLService {
  /**
   * Extracts filename from a path or URL
   *
   * @param path - File path or URL (may include query params)
   * @returns Filename or default "video.mp4" if extraction fails
   *
   * @example
   * getVideoFileName("/outputs/video_123.mp4") // Returns "video_123.mp4"
   * getVideoFileName("http://example.com/video.mp4?token=xyz") // Returns "video.mp4"
   * getVideoFileName(undefined) // Returns "video.mp4"
   */
  static getVideoFileName(path?: string): string {
    if (!path) return 'video.mp4';
    // If it's a URL with query params, extract just the filename
    let filename = path;
    if (path.includes('?')) {
      filename = path.split('?')[0];
    }
    // Get the last part after / or \
    const parts = filename.split(/[/\\]/);
    return parts[parts.length - 1] || 'video.mp4';
  }

  /**
   * Checks if the current environment is local development mode.
   * Delegates to centralized apiMode utility for consistent behavior.
   *
   * @returns true if running in local mode (localhost, Electron, or VITE_API_MODE=local)
   *
   * @example
   * isLocalMode() // Returns true on localhost:5173
   * isLocalMode() // Returns true in Electron desktop app
   * isLocalMode() // Returns false on production domain
   */
  static isLocalMode(): boolean {
    return checkLocalMode();
  }

  /**
   * Constructs full URL for a given path, handling local vs production mode
   *
   * @param path - Relative or absolute URL path
   * @returns Full URL with correct origin/base for current environment
   *
   * @example
   * // Local mode (localhost):
   * getFullUrl("/outputs/video.mp4") // Returns "http://localhost:5001/outputs/video.mp4"
   *
   * // Production mode:
   * getFullUrl("/outputs/video.mp4") // Returns "https://example.com/outputs/video.mp4"
   *
   * // External URL (unchanged):
   * getFullUrl("https://s3.aws.com/video.mp4") // Returns "https://s3.aws.com/video.mp4"
   */
  static getFullUrl(path: string): string {
    // If already a full URL, return as-is
    if (!path.startsWith('/')) {
      return path;
    }

    // Use centralized URL resolver for all environments
    // This correctly handles local, staging, and production modes
    return `${getApiBaseUrl()}${path}`;
  }

  /**
   * Gets the appropriate API base URL for current environment
   * Delegates to centralized urlResolver for consistent behavior.
   *
   * @returns API base URL (local backend, serverless API, or fallback to local)
   *
   * @example
   * // Local mode:
   * getAPIBaseUrl() // Returns "http://localhost:5001"
   *
   * // Production/Staging mode with VITE_SERVERLESS_API_URL:
   * getAPIBaseUrl() // Returns "https://aigod-api-staging-fxijzfmlsq-uc.a.run.app"
   */
  static getAPIBaseUrl(): string {
    return getApiBaseUrl();
  }
}
